import { useCallback, useMemo, useState } from 'react';
import { css } from '@emotion/react';
import { Person } from '@weave/schema-gen-ts/dist/schemas/persons/v3/persons.pb';
import { useTranslation } from 'react-i18next';
import { getUser } from '@frontend/auth-helpers';
import { Icon } from '@frontend/icons';
import {
  MultiTabTemplateSelector,
  UseTemplateFlowResult,
  useUpdateTemplateLastTimestamp,
} from '@frontend/integrated-messaging';
import { breakpoints, useMatchMedia } from '@frontend/responsiveness';
import { theme } from '@frontend/theme';
import {
  DropdownField,
  Modal,
  ModalControlResponse,
  SearchField,
  Tabs,
  useFormField,
  Text,
} from '@frontend/design-system';
import { CreateNewTemplateCTA } from '../atoms/create-new-template-cta';
import { useTemplateTypeModalMap } from '../hooks/use-template-type-modal-map';
import { useTemplateMessagingStore } from '../stores/template-selector-store';
import { NewTemplateFlow } from './new-template-flow';
import { TemplateSelectorModal } from './template-selector-modal';

interface InboxTemplateResponsiveModalProps {
  modalControl: ModalControlResponse;
  templateTypeProps: Record<string, UseTemplateFlowResult>;
  person?: Person;
}

export const InboxTemplateResponsiveModal = ({
  modalControl,
  templateTypeProps,
  person,
}: InboxTemplateResponsiveModalProps) => {
  const { setSelectedMessageTemplateType, selectedMessageTemplateType } = useTemplateMessagingStore(
    'setSelectedMessageTemplateType',
    'selectedMessageTemplateType'
  );
  const { closeModal, modalProps } = modalControl;
  const isLargeMode = useMatchMedia({ minWidth: breakpoints.xsmall.max });
  const [controlledTab, setControlledTab] = useState<string>('All');
  const [showNewTemplateFlow, setShowNewTemplateFlow] = useState<boolean>(false);
  const [customEditTemplateId, setCustomEditTemplateId] = useState<string>('');
  const { t } = useTranslation('integrated-messaging');
  const searchFieldProps = useFormField({
    type: 'text',
  });
  const { userTemplateSortingPreferences: templateSortingPreference, setTemplateSortingPreference } =
    useUpdateTemplateLastTimestamp();
  const user = getUser();
  const tabItems = useMemo(
    () => Object.values(templateTypeProps).map((item: UseTemplateFlowResult) => item.tabItem),
    [templateTypeProps]
  );
  const tabItemMap = useMemo(() => {
    return tabItems.reduce<Record<string, UseTemplateFlowResult['tabItem']>>((acc, item) => {
      if (item.tabLabel) acc[item.tabLabel] = item;
      return acc;
    }, {});
  }, [tabItems]);

  const templateTypeModalMap = useTemplateTypeModalMap(tabItemMap);
  const activeModalConfig = selectedMessageTemplateType ? templateTypeModalMap[selectedMessageTemplateType] : undefined;

  const userTemplateSortingPreference =
    (user?.userID ? templateSortingPreference[user?.userID] : undefined) ?? 'most-recent';
  const { onChange: _sortFieldOnChange, ...otherSortFieldProps } = useFormField(
    {
      type: 'dropdown',
      value: userTemplateSortingPreference,
    },
    [userTemplateSortingPreference]
  );
  const sortFieldOnChange = useCallback<typeof _sortFieldOnChange>(
    (e) => {
      if (!e || !('value' in e)) return;
      const typedEvent = e as { value: typeof userTemplateSortingPreference; name: string };
      if (user?.userID) setTemplateSortingPreference({ userId: user.userID, newPreference: typedEvent.value });
      _sortFieldOnChange(e);
    },
    [setTemplateSortingPreference, _sortFieldOnChange, user?.userID]
  );

  const templateItemEditHandler = useCallback(
    (templateId: string) => {
      setCustomEditTemplateId(templateId);
    },
    [setCustomEditTemplateId]
  );

  const handleBack = useCallback(() => {
    setShowNewTemplateFlow(false);
    setCustomEditTemplateId('');
  }, [setShowNewTemplateFlow, setCustomEditTemplateId]);

  const closeAllModal = useCallback(() => {
    setSelectedMessageTemplateType();
    closeModal();
  }, [setSelectedMessageTemplateType, closeModal]);

  // NEW TEMPLATE CREATION AND EDIT EXISTING TEMPLATE FLOW
  if (showNewTemplateFlow || customEditTemplateId) {
    return (
      <NewTemplateFlow
        mode={!!customEditTemplateId ? 'edit' : 'create'}
        key={customEditTemplateId}
        modalProps={modalProps}
        onBack={handleBack}
        currentTab={tabItemMap?.[controlledTab]?.templateSelectorProps.templateTypes?.[0] ?? ''}
        customEditTemplateId={customEditTemplateId}
      />
    );
  }

  // PROGRESSIVE FLOW FOR FORM & SCHEDULE TEMPLATES
  if (activeModalConfig?.Component && activeModalConfig.data) {
    const { Component, data, additionalProps } = activeModalConfig;

    return <Component {...additionalProps} {...data.templateSelectorProps} show onClose={closeAllModal} />;
  }

  // RESPONSIVE MODAL FOR ALL TEMPLATE TYPES
  return (
    <TemplateSelectorModal {...modalProps}>
      <Modal.Header
        onClose={closeModal}
        css={[
          { padding: theme.spacing(4) },
          isLargeMode && {
            padding: theme.spacing(1, 4, 0),
          },
        ]}
      >
        {t('Message Templates')}
      </Modal.Header>
      {person && (
        <div
          css={[
            {
              display: 'flex',
              marginTop: `-${theme.spacing(1)}`,
              padding: theme.spacing(0, 3),
              gap: theme.spacing(0.5),
            },
            isLargeMode && {
              marginTop: '0',
              padding: theme.spacing(0, 4),
            },
          ]}
        >
          <Text size='small'>{`To:`}</Text>
          <Text weight={'bold'} size='small'>{`${person.firstName} ${person.lastName}`}</Text>
        </div>
      )}
      <div
        css={[
          {
            display: 'flex',
            width: '100%',
            gap: theme.spacing(1),
            padding: theme.spacing(2, 3, 1),
          },
          isLargeMode && {
            padding: theme.spacing(3, 4, 1),
          },
        ]}
      >
        <SearchField containerCss={css({ flexGrow: 1 })} name={t('Template search')} {...searchFieldProps} autoFocus />
        <DropdownField
          onChange={sortFieldOnChange}
          {...otherSortFieldProps}
          name={t('Sort by')}
          label=''
          startAdornment={<Icon name='sort-ascending' size={20} css={{ marginRight: theme.spacing(1) }} />}
          css={{ p: { paddingRight: theme.spacing(1) }, minWidth: 178 }}
        >
          <DropdownField.Option value='most-recent'>{t('Most Recent')}</DropdownField.Option>
          <DropdownField.Option value='a-z'>{t('A - Z')}</DropdownField.Option>
          <DropdownField.Option value='z-a'>{t('Z - A')}</DropdownField.Option>
        </DropdownField>
      </div>

      <Tabs controlledTab={controlledTab} onChange={(id) => setControlledTab(id)}>
        <Tabs.Bar fullWidth>
          {tabItems.map((panel) => (
            <Tabs.Tab key={panel.tabLabel} id={panel.tabLabel || ''}>
              {panel.tabLabel}
            </Tabs.Tab>
          ))}
        </Tabs.Bar>
        <div css={[{ overflow: 'auto', paddingBottom: theme.spacing(8) }, isLargeMode && { flex: 1 }]}>
          {tabItems.map((panel) => {
            return (
              <Tabs.Panel key={panel.tabLabel} controller={panel.tabLabel || ''}>
                <MultiTabTemplateSelector
                  {...panel.templateSelectorProps}
                  externalSearchValue={searchFieldProps.value}
                  hideSearch
                  hideNewTemplateCTA
                  onEdit={templateItemEditHandler}
                  tabItemMap={tabItemMap}
                />
              </Tabs.Panel>
            );
          })}
        </div>
      </Tabs>
      <Modal.StickyFooter>
        <CreateNewTemplateCTA
          onClick={() => setShowNewTemplateFlow(true)}
          currentTab={controlledTab !== 'All' ? controlledTab : ''}
          customCss={isLargeMode && { justifyContent: 'flex-start' }}
        />
      </Modal.StickyFooter>
    </TemplateSelectorModal>
  );
};
